import { PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import useAttachments from '@hooks/use-attachments';
import useAuth from '@hooks/use-auth';
import useClipboardPaste from '@hooks/use-clipboard-paste';
import useLastCallback from '@hooks/use-last-callback';
import useSocket from '@hooks/use-socket';
import { useStateRef } from '@hooks/use-state-ref';
import useSyncEffect from '@hooks/use-sync-effect';
import type { Attachment } from '@interfaces/attachment';
import type { Message } from '@interfaces/message';
import type { MessageTemplate } from '@interfaces/message-template';
import { removeInputMessageReplyInfo } from '@store/reducers/messages';
import { selectInputMessageReplyInfo } from '@store/selectors/messages';
import { validateFiles } from '@utils/file';
import focusEditableElement from '@utils/focus-editable-element';
import parseMessageInput from '@utils/parse-message-input';
import { insertHtmlInSelection } from '@utils/selection';
import { openSystemFilesDialog } from '@utils/ui/system-files-dialog';
import { Button } from 'antd';
import type { FC, RefObject } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useBoundStore } from '@/newstore';
import { useMessageActions } from '@/newstore/selectors/messages';
import { useSelector } from '@/store';

import AttachmentModal from './AttachmentModal';
import styles from './Composer.module.scss';
import ComposerEmbeddedMessage from './ComposerEmbeddedMessage';
import { isSelectionInsideInput } from './helpers/selection';
import MessageInput from './MessageInput';
import type { PopoverTemplate } from './MessageTemplatesPopover';
import MessageTemplatesPopover from './MessageTemplatesPopover';

interface OwnProps {
  conversationId: number;
  conversationLanguageCode: string;
  editableInputId: string;
  editableInputCssSelector: string;
  containerRef: RefObject<HTMLDivElement>;
}

interface StateProps {
  messageTemplates: PopoverTemplate[];
  replyToMessage?: Message;
  isForwarding: boolean;
}

const NonMemoComposer: FC<Omit<OwnProps, 'conversationLanguageCode'> & StateProps> = ({
  conversationId,
  editableInputId,
  editableInputCssSelector,
  containerRef,
  messageTemplates,
  replyToMessage,
  isForwarding,
}) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState('');
  const auth = useAuth();
  const socket = useSocket();
  const [slashCommand, setSlashCommand] = useState<string | null>(null);
  const hasMessageTemplates = messageTemplates && messageTemplates.length > 0;
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const hasAttachments = Boolean(attachments.length);

  const { sendMessage, sendAttachments, forwardMessage } = useMessageActions();

  const getHtml = useCallback(() => html, [html]);

  useEffect(() => {
    if (html.startsWith('/')) {
      setSlashCommand(html.slice(1));
    } else {
      setSlashCommand(null);
    }
  }, [html]);

  useSyncEffect(
    ([prevReplyToMessage]) => {
      if (!prevReplyToMessage && replyToMessage) {
        focusEditableElement(inputRef.current!);
      }
    },
    [replyToMessage],
  );

  const insertHtmlAndUpdateCursor = useLastCallback((newHtml: string, inInputId: string = editableInputId) => {
    const selection = window.getSelection()!;
    let messageInput: HTMLDivElement;
    if (inInputId === editableInputId) {
      messageInput = document.querySelector<HTMLDivElement>(editableInputCssSelector)!;
    } else {
      messageInput = document.getElementById(editableInputId) as HTMLDivElement;
    }

    if (selection.rangeCount) {
      const selectionRange = selection.getRangeAt(0);
      if (isSelectionInsideInput(selectionRange, editableInputId)) {
        insertHtmlInSelection(newHtml);
        messageInput.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
    }

    setHtml(`${html}${newHtml}`);
  });

  const resetComposer = useLastCallback(() => {
    setHtml('');
    setTimeout(setAttachments, 0, []);
    removeInputMessageReplyInfo();
  });

  const resetComposerRef = useStateRef(resetComposer);
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      resetComposerRef.current();
    };
  }, [conversationId, resetComposerRef]);

  const handleSend = useLastCallback(async () => {
    const currentUserId = auth.getUserId();
    const authorName = auth.getAuthorName();
    const content = parseMessageInput(html.replace(/<br\s*\/?>/gi, '\n').trim());
    if (currentUserId === undefined) {
      return;
    }

    if (attachments.length > 0) {
      // TODO: merge sending plain message and message with attachment, move/refactor sending to store actions
      // noinspection ES6MissingAwait
      sendAttachments(
        auth,
        socket,
        conversationId,
        currentUserId,
        attachments,
        content,
        auth.getAuthorName(),
        replyToMessage?.id,
      );

      resetComposer();

      return;
    }

    if (!content && !isForwarding) {
      return;
    }

    if (content.length > 0) {
      await sendMessage(auth, socket, conversationId, content, currentUserId, authorName, replyToMessage?.id);
    }

    if (isForwarding) {
      // noinspection ES6MissingAwait
      forwardMessage(auth, socket, currentUserId, authorName);
    }

    resetComposer();
    // focus?
  });

  const onSend = handleSend;
  const mainButtonHandler = handleSend;

  const mainButtonDisabled = !isForwarding && html === '' && attachments.length === 0;

  const { handleFileSelect, handleClearAttachments, handleSetAttachments } = useAttachments(
    attachments,
    setAttachments,
  );

  useClipboardPaste(insertHtmlAndUpdateCursor, handleSetAttachments, setHtml);

  const onFileSelect = useLastCallback((e: Event) => {
    const { files } = e.target as HTMLInputElement;
    const validatedFiles = validateFiles(files);

    if (!validatedFiles?.length) {
      return;
    }

    // noinspection JSIgnoredPromiseFromCall
    handleFileSelect(validatedFiles);
  });

  const handleCaptionUpdate = useLastCallback((caption: string) => {
    setHtml(caption);
  });

  return (
    <div className={styles.composer}>
      <AttachmentModal
        conversationId={conversationId}
        attachments={attachments}
        getHtml={() => html}
        onSend={handleSend}
        onCaptionUpdate={handleCaptionUpdate}
        onClear={handleClearAttachments}
      />
      <div className={styles.composerWrapper}>
        <ComposerEmbeddedMessage chatId={conversationId} />
        <div className={styles.composerBottomWrapper}>
          <div className={styles.messageInputWrapper}>
            {hasMessageTemplates && (
              <MessageTemplatesPopover
                command={slashCommand}
                templates={messageTemplates}
                className={styles.templatesButton}
                popupContainerRef={containerRef}
                onTemplateSelected={(template) => {
                  setHtml(template.replace(/\r?\n/g, '<br />'));
                  focusEditableElement(inputRef.current!);
                }}
                onClose={() => {
                  if (html.startsWith('/')) {
                    setHtml('');
                  }
                }}
              />
            )}
            <MessageInput
              ref={inputRef}
              id="message-input-text"
              isActive={!hasAttachments}
              editableInputId={editableInputId}
              conversationId={conversationId}
              getHtml={getHtml}
              placeholder="Type a message"
              canAutoFocus={true}
              onUpdate={setHtml}
              onSend={onSend}
              withMessageTemplatesButton={hasMessageTemplates}
            />
            <Button
              className={styles.attachButton}
              type="link"
              icon={<PaperClipOutlined style={{ fontSize: '1.2rem' }} />}
              onClick={() => openSystemFilesDialog('*', onFileSelect)}
            />
          </div>
          <Button
            type="primary"
            size="large"
            className={styles.mainButton}
            disabled={mainButtonDisabled}
            onClick={mainButtonHandler}
            icon={<SendOutlined />}
          />
        </div>
      </div>
    </div>
  );
};

const Composer = memo<OwnProps>(function Composer(props: OwnProps) {
  const templateSelector = useCallback(
    (templates?: MessageTemplate[]) =>
      templates?.map((template) => ({
        id: template.id,
        command: template.slug,
        template:
          template.translations.find((translation) => translation.language_code === props.conversationLanguageCode)
            ?.template || template.translations.find((translation) => translation.language_code === 'en')!.template,
      })) || [],
    [props.conversationLanguageCode],
  );
  // TODO: applying selector function directly to useSelector has no effect, investigate
  const templates = useSelector<MessageTemplate[], MessageTemplate[]>('message-templates', (state) => state);
  const messageTemplates = templateSelector(templates);

  const inputMessageReplyInfo = selectInputMessageReplyInfo();
  const forwardedMessage = useBoundStore((state) => state.forwardedMessage);

  return (
    <NonMemoComposer
      conversationId={props.conversationId}
      editableInputId={props.editableInputId}
      editableInputCssSelector={props.editableInputCssSelector}
      containerRef={props.containerRef}
      messageTemplates={messageTemplates}
      replyToMessage={inputMessageReplyInfo}
      isForwarding={props.conversationId === forwardedMessage?.toChatId}
    />
  );
});

export default Composer;
